import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {tomorrow} from 'react-syntax-highlighter/dist/esm/styles/prism';
import {API_BASE_URL} from '../services/api';
import tcgDataService from '../services/tcgDataService';

const Documentation = () => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supportedTcgs, setSupportedTcgs] = useState([]);
  const [expandedExamples, setExpandedExamples] = useState({});
  const [apiExamples, setApiExamples] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch endpoints and examples using cached data service
        const [endpointsData, examplesData] = await Promise.all([
          tcgDataService.getDocEndpoints().catch(() => null),
          tcgDataService.getDocExamples().catch(() => null)
        ]);

        if (endpointsData) {
          setEndpoints(endpointsData.endpoints);
          setSupportedTcgs(endpointsData.supportedTcgs);
        } else {
          setError('Failed to fetch endpoints');
          // Fallback to static endpoints if API fails
          setEndpoints([
            {
              category: 'TCG Data',
              endpoints: [
                {
                  method: 'GET',
                  path: '/api/v1/tcg',
                  description: 'Get list of available TCGs',
                  auth: 'API Key',
                  public: true
                }
              ]
            }
          ]);
        }

        if (examplesData) {
          setApiExamples(examplesData.examples);
        } else {
          console.warn('Failed to fetch API examples');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load API data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Schedule automatic revalidation every 12 hours
    const cleanup = tcgDataService.scheduleRevalidation(() => {
      // Refresh data in UI when revalidation happens
      fetchData();
    });

    return cleanup; // Cleanup interval on unmount
  }, []);

  const toggleExample = (endpointKey) => {
    setExpandedExamples(prev => ({
      ...prev,
      [endpointKey]: !prev[endpointKey]
    }));
  };

  const getExampleData = (endpoint) => {
    if (!apiExamples) {
      // Fallback if examples haven't loaded yet
      return {
        request: `curl -X ${endpoint.method} "https://api.tcg-api.eu${endpoint.path}" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
        response: `{
  "success": true,
  "message": "Loading examples...",
  "data": {}
}`
      };
    }

    const path = endpoint.path;

    // Handle general TCG list endpoint
    if (path.includes('/tcg') && !path.includes('/tcg/')) {
      const example = apiExamples.general?.tcg_list;
      if (example) {
        return {
          request: example.request.curl,
          response: JSON.stringify(example.response, null, 2)
        };
      }
    }

    // Extract TCG name from path for TCG-specific endpoints
    const tcgMatch = path.match(/\/tcg\/([^/]+)/);
    if (!tcgMatch) {
      return {
        request: `curl -X ${endpoint.method} "https://api.tcg-api.eu${path}" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
        response: `{
  "success": true,
  "message": "No example available",
  "data": {}
}`
      };
    }

    const tcgName = tcgMatch[1];
    const tcgExamples = apiExamples.tcgs?.[tcgName]?.endpoints;

    if (!tcgExamples) {
      return {
        request: `curl -X ${endpoint.method} "https://api.tcg-api.eu${path}" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
        response: `{
  "success": true,
  "message": "No examples available for this TCG",
  "data": {}
}`
      };
    }

    // Determine which example to use based on the endpoint
    // Order matters - check more specific patterns first
    let exampleKey = null;
    if (path.includes('/sets/{setId}/cards')) {
      exampleKey = 'set_cards';
    } else if (path.includes('/cards/{cardId}')) {
      exampleKey = 'card';
    } else if (path.includes('/sets/{setId}')) {
      exampleKey = 'set';
    } else if (path.includes('/search')) {
      exampleKey = 'search';
    } else if (path.includes('/sets') && !path.includes('{')) {
      exampleKey = 'sets';
    } else if (path.includes('/cards') && !path.includes('{')) {
      exampleKey = 'cards';
    }

    const example = tcgExamples[exampleKey];
    if (example) {
      return {
        request: example.request.curl,
        response: JSON.stringify(example.response, null, 2)
      };
    }

    // Debug logging
    console.log('Debug info:', {
      path,
      tcgName,
      exampleKey,
      availableKeys: Object.keys(tcgExamples),
      tcgExamples
    });

    // Fallback
    return {
      request: `curl -X ${endpoint.method} "https://api.tcg-api.eu${path}" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
      response: `{
  "success": true,
  "message": "Example not found",
  "data": {}
}`
    };
  };

  const examples = {
    getTcgs: `curl -X GET "https://api.tcg-api.eu/api/v1/tcg" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
    
    getSets: `curl -X GET "https://api.tcg-api.eu/api/v1/tcg/pokemon/sets?page=1&limit=20" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
    
    getCards: `curl -X GET "https://api.tcg-api.eu/api/v1/tcg/pokemon/cards?set=base1&page=1&limit=20" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
    
    searchCards: `curl -X GET "https://api.tcg-api.eu/api/v1/tcg/pokemon/search?q=pikachu&rarity=rare" \\
  -H "X-API-Key: tcg_your_api_key_here"`,

    javascript: `const apiKey = 'tcg_your_api_key_here';
const baseURL = '${API_BASE_URL}';

// Get available TCGs
const tcgs = await fetch(\`\${baseURL}/tcg\`, {
  headers: { 'X-API-Key': apiKey }
}).then(res => res.json());

// Search for cards
const cards = await fetch(\`\${baseURL}/tcg/pokemon/search?q=charizard\`, {
  headers: { 'X-API-Key': apiKey }
}).then(res => res.json());`,

    python: `import requests

api_key = 'tcg_your_api_key_here'
base_url = '${API_BASE_URL}'
headers = {'X-API-Key': api_key}

# Get Pokemon sets
response = requests.get(f'{base_url}/tcg/pokemon/sets', headers=headers)
sets = response.json()

# Search for cards
params = {'q': 'charizard', 'type': 'fire'}
response = requests.get(f'{base_url}/tcg/pokemon/search', headers=headers, params=params)
cards = response.json()`
  };

  const renderEndpoint = (endpoint) => {
    const endpointKey = `${endpoint.method}-${endpoint.path}`;
    const isExpanded = expandedExamples[endpointKey];
    const exampleData = getExampleData(endpoint);

    return (
      <div key={endpointKey} className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
              endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
              endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {endpoint.method}
            </span>
            <code className="text-sm font-mono">{endpoint.path}</code>
          </div>
          <button
            onClick={() => toggleExample(endpointKey)}
            className="btn btn-sm btn-outline-primary"
            disabled={!apiExamples}
          >
            <span>{apiExamples ? 'Try Example' : 'Loading examples...'}</span>
            <svg
              className={`w-4 h-4 ml-1 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <p className="text-gray-600 text-sm mb-2">{endpoint.description}</p>

        {endpoint.pathParams && endpoint.pathParams.length > 0 && (
          <div className="mb-2">
            <p className="text-gray-500 text-xs font-medium mb-1">Path Parameters:</p>
            <div className="space-y-1">
              {endpoint.pathParams.map(param => (
                <div key={param.name} className="text-xs text-gray-500">
                  <code className="bg-gray-100 px-1 rounded">{param.name}</code>: {param.description}
                  {param.example && <span className="ml-1">(e.g., "{param.example}")</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {endpoint.params && endpoint.params.length > 0 && (
          <div className="mb-2">
            <p className="text-gray-500 text-xs font-medium mb-1">Query Parameters:</p>
            <div className="space-y-1">
              {endpoint.params.map(param => (
                <div key={param.name} className="text-xs text-gray-500">
                  <code className="bg-gray-100 px-1 rounded">{param.name}</code>
                  {param.required && <span className="text-red-500 ml-1">*</span>}: {param.description}
                  {param.default !== undefined && <span className="ml-1">(default: {param.default})</span>}
                  {param.example && <span className="ml-1">(e.g., "{param.example}")</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {isExpanded && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-7-4c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                  </svg>
                  Example Request
                </h4>
                <SyntaxHighlighter language="bash" style={tomorrow} className="!mt-0 !mb-0">
                  {exampleData.request}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Example Response
                </h4>
                <SyntaxHighlighter language="json" style={tomorrow} className="!mt-0 !mb-0">
                  {exampleData.response}
                </SyntaxHighlighter>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">API Documentation</h1>
        <p className="text-gray-600 mt-2">
          Complete guide to using the TCG API with examples and detailed endpoint documentation.
        </p>
      </div>

      {/* Authentication */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication</h2>
        
        <div className="space-y-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900">API Key (Recommended)</h3>
            <p className="text-gray-600 text-sm mt-1">
              Include your API key in the <code className="bg-gray-100 px-1 rounded">X-API-Key</code> header.
            </p>
            <div className="mt-3">
              <SyntaxHighlighter language="bash" style={tomorrow} className="!mt-0">
                {`curl -H "X-API-Key: tcg_your_api_key_here" https://api.tcg-api.eu/api/v1/tcg`}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Rate Limits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900">With API Key</h3>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>Daily: 10,000 requests</li>
              <li>Per minute: 100 requests</li>
            </ul>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900">Without API Key</h3>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>Daily: 1,000 requests</li>
              <li>Per minute: 10 requests</li>
            </ul>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-gray-600 text-sm mb-2">
            Rate limit information is included in response headers: <code>X-RateLimit-Remaining-Daily</code> and <code>X-RateLimit-Remaining-Minute</code>.
          </p>
          <p className="text-gray-600 text-sm">
            When rate limit is exceeded, the response includes <code>retryAfter</code> field with seconds until next request is allowed.
          </p>
        </div>
      </div>

      {/* Postman Collection */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Postman Collection</h2>
        <p className="text-gray-600 mb-4">
          Get started quickly with our ready-to-use Postman collection:
        </p>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <a
              href={`${API_BASE_URL}/docs/postman`}
              download="tcg-api-postman-collection.json"
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Download Collection JSON
            </a>
            <a
              href="https://www.postman.com/tdruben/tdr-tcg-api/collection/wcncnt6/tcg-api-public-endpoints"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              View in Postman Web
            </a>
          </div>
          <p className="text-gray-500 text-sm">
            The collection includes all public endpoints with authentication setup and example requests for each supported TCG.
          </p>
        </div>
      </div>

      {/* Supported TCGs */}
      {supportedTcgs.length > 0 && (
        <div className="card p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Supported TCGs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {supportedTcgs.map(tcg => (
              <div key={tcg.name} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{tcg.display_name}</h3>
                  <Link
                    to={`/docs/${tcg.name.toLowerCase()}`}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Docs →
                  </Link>
                </div>
                {tcg.description && (
                  <p className="text-gray-600 text-sm mt-1">{tcg.description}</p>
                )}
                <code className="text-xs text-gray-500 mt-2 block">
                  /api/v1/tcg/{tcg.name.toLowerCase()}/{'{endpoints}'}
                </code>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              Each TCG has dedicated documentation with specific examples and endpoints.
            </p>
          </div>
        </div>
      )}


      {/* Endpoints */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">API Endpoints</h2>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading endpoints...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && endpoints.length > 0 && (
          <div className="space-y-8">
            {endpoints.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  {category.category}
                </h3>
                <div className="space-y-4">
                  {category.endpoints.map(renderEndpoint)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Examples */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Code Examples</h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">cURL Examples</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Get Available TCGs</h4>
                <SyntaxHighlighter language="bash" style={tomorrow}>
                  {examples.getTcgs}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Get Pokemon Sets</h4>
                <SyntaxHighlighter language="bash" style={tomorrow}>
                  {examples.getSets}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Search Cards</h4>
                <SyntaxHighlighter language="bash" style={tomorrow}>
                  {examples.searchCards}
                </SyntaxHighlighter>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">JavaScript</h3>
            <SyntaxHighlighter language="javascript" style={tomorrow}>
              {examples.javascript}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Python</h3>
            <SyntaxHighlighter language="python" style={tomorrow}>
              {examples.python}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>

      {/* Response Format */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Response Format</h2>
        <p className="text-gray-600 mb-4">All API responses follow a consistent JSON format:</p>
        
        <SyntaxHighlighter language="json" style={tomorrow}>
{`{
  "success": true,
  "message": "Request completed successfully",
  "data": {
    // Response data here
  }
}`}
        </SyntaxHighlighter>

        <p className="text-gray-600 mt-4 text-sm">
          For paginated responses, additional pagination information is included in the data object.
        </p>
      </div>

      {/* Error Handling */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Handling</h2>
        <p className="text-gray-600 mb-4">Error responses include appropriate HTTP status codes:</p>
        
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <code>400</code>
            <span className="text-gray-600">Bad Request - Invalid parameters</span>
          </div>
          <div className="flex justify-between">
            <code>401</code>
            <span className="text-gray-600">Unauthorized - Missing or invalid API key</span>
          </div>
          <div className="flex justify-between">
            <code>403</code>
            <span className="text-gray-600">Forbidden - IP blocked or insufficient permissions</span>
          </div>
          <div className="flex justify-between">
            <code>404</code>
            <span className="text-gray-600">Not Found - Resource doesn't exist</span>
          </div>
          <div className="flex justify-between">
            <code>429</code>
            <span className="text-gray-600">Too Many Requests - Rate limit exceeded</span>
          </div>
          <div className="flex justify-between">
            <code>500</code>
            <span className="text-gray-600">Internal Server Error</span>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="font-semibold text-gray-900 mb-2">Rate Limit Error Example</h3>
          <SyntaxHighlighter language="json" style={tomorrow} className="!mt-0">
{`{
  "success": false,
  "message": "Rate limit exceeded",
  "error": {
    "type": "RATE_LIMIT_EXCEEDED",
    "limit": 100,
    "current": 100,
    "resetTime": "2024-01-01T12:01:00.000Z",
    "retryAfter": 45
  }
}`}
          </SyntaxHighlighter>
          <p className="text-gray-600 text-sm mt-2">
            The <code>retryAfter</code> field indicates how many seconds to wait before making the next request.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Documentation;